'use client'

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getUserPreferences,
  updateOrderedSportsbooks,
  updateSportsbookParticipation,
  updateSportPreferences,
} from '@/lib/supabase/preferences-actions'
import type { Sport, SportPreferences, SportsbookParticipation, UserPreferences, Theme } from '@sharpr/types'

// Stable module-level fallbacks so React useEffect dependency checks don't
// see new array references on every render while preferences are loading.
const EMPTY_ARRAY: string[] = []
const EMPTY_PARTICIPATION: Record<string, SportsbookParticipation> = {}
const EMPTY_SPORT_PREFERENCES: Partial<Record<Sport, SportPreferences>> = {}
const FALLBACK_PREFERENCES = {
  ordered_sportsbooks: EMPTY_ARRAY,
  sportsbook_participation: EMPTY_PARTICIPATION,
  hidden_sportsbooks: EMPTY_ARRAY,
  excluded_from_best_line: EMPTY_ARRAY,
  theme: 'dark' as Theme,
  sportPreferences: EMPTY_SPORT_PREFERENCES,
} as const

// Shared mutation scope — React Query v5 serializes mutations within the same scope
// across hook instances. Without this, two components mounting useUserPreferences
// can race and the later mutation's setQueryData overwrites the earlier's composed result.
const USER_PREFERENCES_SCOPE = { id: 'userPreferences' } as const

/**
 * Hook to access and manage user preferences (theme, sportsbook order, hidden books,
 * per-sport hidden markets). Uses React Query for caching and optimistic updates.
 */
export function useUserPreferences() {
  const queryClient = useQueryClient()

  const {
    data: preferencesResult,
    isLoading,
    error: fetchError,
    refetch,
  } = useQuery({
    queryKey: ['userPreferences'],
    queryFn: async () => {
      const result = await getUserPreferences()
      if (result.error) {
        throw new Error(result.error)
      }
      return result.data as UserPreferences
    },
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })

  const orderedSportsbooksMutation = useMutation({
    scope: USER_PREFERENCES_SCOPE,
    mutationFn: async (sportsbooks: string[]) => {
      if (typeof window !== 'undefined') {
        localStorage.setItem('sportsbook_order', JSON.stringify(sportsbooks))
      }

      const result = await updateOrderedSportsbooks(sportsbooks)
      if (result.error) {
        throw new Error(result.error)
      }
      return result.data as UserPreferences
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['userPreferences'], data)
    },
  })

  const sportsbookParticipationMutation = useMutation({
    scope: USER_PREFERENCES_SCOPE,
    mutationFn: async (participation: Record<string, SportsbookParticipation>) => {
      const result = await updateSportsbookParticipation(participation)
      if (result.error) {
        throw new Error(result.error)
      }
      return result.data as UserPreferences
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['userPreferences'], data)
    },
  })

  const sportPreferencesMutation = useMutation({
    scope: USER_PREFERENCES_SCOPE,
    mutationFn: async ({ sport, patch }: { sport: Sport; patch: Partial<SportPreferences> }) => {
      const result = await updateSportPreferences(sport, patch)
      if (result.error) {
        throw new Error(result.error)
      }
      return result.data as UserPreferences
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['userPreferences'], data)
    },
  })

  return {
    preferences: preferencesResult || FALLBACK_PREFERENCES,

    loading: isLoading,
    error: fetchError,

    updateOrderedSportsbooks: orderedSportsbooksMutation.mutateAsync,
    updateSportsbookParticipation: sportsbookParticipationMutation.mutateAsync,
    updateSportPreferences: sportPreferencesMutation.mutateAsync,

    isUpdatingOrderedSportsbooks: orderedSportsbooksMutation.isPending,
    isUpdatingSportsbookParticipation: sportsbookParticipationMutation.isPending,
    isUpdatingSportPreferences: sportPreferencesMutation.isPending,

    orderedSportsbooksError: orderedSportsbooksMutation.error,
    sportsbookParticipationError: sportsbookParticipationMutation.error,
    sportPreferencesError: sportPreferencesMutation.error,

    refetch,
  }
}
